import FailedPaymentSVG from '@birdi/icons/failed-payment.svg';
import ThankYouSVG from '@birdi/icons/thank-you.svg';
import { css } from '@emotion/react';
import { BirdiTheme, breakpoints, buttonBlue } from '@birdi/theme';
import * as Dialog from '@radix-ui/react-dialog';
import CloseSVG from '@birdi/icons/close.svg';
import { useEffect, useState } from 'react';
import { portalCSS } from '@birdi/modal-kit';
import { closeButtonCSS, contentCSS } from '@birdi/modal-kit';


export const SubscriptionPaymentModal = () => {
  const [status, setStatus] = useState('');

  const onCloseHandler = () => {
    window.history.replaceState(null, '', window.location.pathname);
    setStatus('');
  };

  useEffect(() => {
    const queryParamsRaw = window.location.href.split('?')[1];
    const queryParams = new URLSearchParams(queryParamsRaw);
    const paymentStatus = queryParams.get('payment_status');
    if (paymentStatus === 'successful') {
      setStatus('successful');
    } else if (paymentStatus === 'unsuccessful') {
      setStatus('unsuccessful');
    } else {
      setStatus('');
    }
  }, [window.location.href]);

  return (
    <Dialog.Root open={!!status} onOpenChange={onCloseHandler}>
      <Dialog.Portal>
        <Dialog.Overlay />
        <div
          className="modal-container"
          css={css`
            ${alertModalCss}
          `}
        >
          <Dialog.Content
            css={(theme: BirdiTheme) => css`
              ${modalContentCss(theme)};
              padding-top: 1em;
            `}
          >
            <header
              css={css`
                ${modalHeaderCss};
                align-content: center;
                flex-direction: row;
                justify-content: center;
              `}
            >
              <Dialog.Close css={dialogCloseCss}>
                <CloseSVG />
              </Dialog.Close>
              <Dialog.Title
                className="request-processing-title"
                id="request-processing-title"
                css={dialogTitleCss}
              >
                {status === 'successful'
                  ? 'Subcription updated successfully!'
                  : 'No update was made to your subscription!'}
              </Dialog.Title>
            </header>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
                text-align: center;
                flex: 1;
              `}
            >
              <div
                css={css`
                  max-width: 80%;
                `}
              >
                {status === 'successful' ? (
                  <SuccessContent />
                ) : (
                  <UnsuccessfulContent />
                )}
              </div>

              <div
                css={css`
                  flex: 1;
                `}
              />
              <p>If you have any queries please contact us via chat.</p>
            </div>
            <button css={buttonCss} onClick={onCloseHandler}>
              Close
            </button>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

const SuccessContent = () => {
  return (
    <>
      <ThankYouSVG width="5em" height="5em" />
    </>
  );
};

const UnsuccessfulContent = () => {
  return (
    <>
      <FailedPaymentSVG width="110px" height="116px" />
      <p>
        If you intended to update your subscription you can try again with the same payment method, or update your payment
        details in your{' '}
        <a
          css={(theme: BirdiTheme) => css`
            color: ${theme.deepOcean};
            text-decoration: underline;
          `}
          href={'/admin-billing'}
        >
          plan overview
        </a>
        .
      </p>
    </>
  );
};

const buttonCss = (theme: BirdiTheme) => css`
  ${buttonBlue(theme, { fullWidth: true, baseSize: 'sm' })};
  border-radius: 5px;
  display: flex;
  align-self: center;
  height: 2.5rem;
  margin-top: 10px;
  margin-bottom: 10px;
  max-width: 20%;
`;

export const alertModalCss = css`
  ${portalCSS}
  padding: 5em;
  padding-top: 2em;
  padding-bottom: 2em;
  overflow-y: hidden;

  @media (min-width: 700px) {
    padding: 15em;
    padding-top: 2em;
    padding-bottom: 2em;
  }

  @media (min-width: 1100px) {
    padding: 20em;
    padding-top: 2em;
    padding-bottom: 2em;
  }
  @media (min-width: 1300px) {
    padding: 30em;
    padding-top: 4em;
    padding-bottom: 4em;
  }

  @media (min-width: 1700px) {
    padding: 40em;
    padding-top: 4em;
    padding-bottom: 4em;
  }
  @media (min-width: 2700px) {
    padding: 50em;
    padding-top: 4em;
    padding-bottom: 4em;
  }
`;

export const modalCss = css`
  ${portalCSS}
  padding: 2em;
  padding-top: 1em;
  padding-bottom: 1em;
  overflow-y: hidden;
  z-index: 1000;

  @media (min-width: ${breakpoints.m}) {
    padding: 2em;
  }
  @media (min-width: ${breakpoints.l}) {
    padding: 8em;
    padding-top: 2em;
    padding-bottom: 2em;
  }
  @media (min-width: 1700px) {
    padding: 18em;
    padding-top: 4em;
    padding-bottom: 4em;
  }
  @media (min-width: 2700px) {
    padding: 30em;
    padding-top: 10em;
    padding-bottom: 10em;
  }
`;

const modalContentCss = (theme: BirdiTheme) => css`
  ${contentCSS(theme)};
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 0;
  padding-top: 1.5em;

  h3 {
    margin-bottom: 0;
  }
`;

const modalHeaderCss = css`
  display: flex;
  flex-direction: column;
  padding-left: 1.5em;
  padding-right: 1.5em;
`;

const dialogCloseCss = css`
  ${closeButtonCSS}
  top: 1.5em;
  right: 1.5em;
`;

const dialogTitleCss = (theme: BirdiTheme) => css`
  margin: 0;
  @media (max-width: ${breakpoints.s}) {
    border-bottom: none;
  }
  padding-bottom: 2px;
`;

const splitContainerCss = (theme: BirdiTheme) => css`
  border-bottom: solid 1px ${theme.mono20};
  background: ${theme.sand};
  margin-top: 5px;
  height: 80vh;
  display: flex;
  overflow: hidden;
`;

const footerCss = css`
  display: flex;
  position: relative;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
`;
